/*
 * Project: Dynamedics Portal Web
 * Created Date: Thursday May 20th 2021
 * Author: Nalinda Wijayagunawardhane
 * -----
 * Last Modified: Thursday May 20th 2021 8:34:38 pm
 * Modified By: Nalinda Wijayagunawardhane at <nwijayagunawardhane@mitrai.com>
 * -----
 * Copyright (c) 2021 Mitra Sparks
 * -----
 * HISTORY:
 * 2021-08-23	NRB	Changed API endpoints.
 * 2021-11-02 SS removed console.errors
 *
 */

import { createSlice } from '@reduxjs/toolkit';
// utils
import axios from '../../utils/axios';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  error: false,
  filters: [],
  dimensions: [],
  workplaceWellbeing: {
    overall: {
      current: 0,
      previous: 0
    },
    departments: [
      { name: '', overall: 0, data: [{ name: '', current: 0, previous: [] }] }
    ],
    diversity: []
  },
  appUsage: [],
  financialOutlook: [],
  contentStats: [],
  productivityData: {
    departments: [{ name: '', data: [{ name: '', data: 0 }] }]
  },
  dailyPulseData: [],
  dailyPulseDataWorkplaces: [],
  employeeEngagement: { overall: { current: 0, previous: 0 }, departments: [] }
};

const slice = createSlice({
  name: 'insights',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    // GET FILTERS SUCCESS
    getFiltersSuccess(state, action) {
      state.isLoading = false;
      state.filters = action.payload;
    },

    // GET WORKPLACE WELLBEING SUCCESS
    getWorkplaceWellbeingSuccess(state, action) {
      state.isLoading = false;
      state.workplaceWellbeing = action.payload;
    },

    // GET APP USAGE SUCCESS
    getAppUsageSuccess(state, action) {
      state.isLoading = false;
      state.appUsage = action.payload;
    },

    // GET FINANCIAL OUTCOMES SUCCESS
    getFinancialOutlookSuccess(state, action) {
      state.isLoading = false;
      state.financialOutlook = action.payload;
    },

    // GET CONTENT STATS SUCCESS
    getContentStatsSuccess(state, action) {
      state.isLoading = false;
      state.contentStats = action.payload;
    },

    // GET PRODUCTIVITY DATA SUCCESS
    getProductivityDataSuccess(state, action) {
      state.isLoading = false;
      state.productivityData = action.payload;
    },

    // GET DAILY PULSE DATA SUCCESS
    getDailyPulseDataSuccess(state, action) {
      state.isLoading = false;
      state.dailyPulseData = action.payload;
    },

    // GET DAILY PULSE DATA WORKPLACES SUCCESS
    getDailyPulseDataWorkplacesSuccess(state, action) {
      state.isLoading = false;
      state.dailyPulseDataWorkplaces = action.payload;
    },

    // GET EMPLOYEE ENGAGEMENT DATA
    getEmployeeEngagementDataSuccess(state, action) {
      state.isLoading = false;
      state.employeeEngagement = action.payload;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const { getMorePosts } = slice.actions;

// ----------------------------------------------------------------------

export function getFilters(filters) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/insights/filters', {
        params: { ...filters }
      });
      dispatch(slice.actions.getFiltersSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getWorkplaceWellbeing(filters) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/insights/workplace-well-being', {
        params: { ...filters }
      });
      dispatch(slice.actions.getWorkplaceWellbeingSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getAppUsage(filters) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/insights/application-usage', {
        params: { ...filters }
      });
      dispatch(slice.actions.getAppUsageSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError());
    }
  };
}

// ----------------------------------------------------------------------

export function getFinancialOutlook(filters) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/insights/financial-outlook', {
        params: { ...filters }
      });
      dispatch(slice.actions.getFinancialOutlookSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError());
    }
  };
}

// ----------------------------------------------------------------------

export function getContentStats(filters) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/insights/content-stats', {
        params: { ...filters }
      });
      dispatch(slice.actions.getContentStatsSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError());
    }
  };
}

// ----------------------------------------------------------------------

export function getProductivityData(filters) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/insights/productivity', {
        params: { ...filters }
      });
      dispatch(slice.actions.getProductivityDataSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError());
    }
  };
}

// ----------------------------------------------------------------------

export function getDailyPulseData(filters) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/insights/daily-pulse', {
        params: { ...filters }
      });
      dispatch(slice.actions.getDailyPulseDataSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError());
    }
  };
}

// ----------------------------------------------------------------------

export function getDailyPulseDataWorkplaces(filters) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/insights/daily-pulse-workplaces', {
        params: { ...filters }
      });
      dispatch(
        slice.actions.getDailyPulseDataWorkplacesSuccess(response.data.data)
      );
    } catch (error) {
      dispatch(slice.actions.hasError());
    }
  };
}

// ----------------------------------------------------------------------

export function getEmployeeEngagement(filters) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get('/insights/employee-engagement', {
        params: { ...filters }
      });
      dispatch(
        slice.actions.getEmployeeEngagementDataSuccess(response.data.data)
      );
    } catch (error) {
      dispatch(slice.actions.hasError());
    }
  };
}
